// ============================================
// MOTION UTILITIES - Framer Motion presets
// ============================================
// Centralized animation variants and utilities for consistent motion design.
// All animations respect prefers-reduced-motion via useReducedMotion hook.

import type { Variants, Transition } from 'framer-motion'

// ============================================
// SPRING PRESETS
// Physics-based spring configurations
// ============================================

export const springs = {
  // Snappy: Quick feedback (buttons, toggles)
  snappy: { type: 'spring', stiffness: 400, damping: 30 } as const,
  // Gentle: Smooth transitions (cards, modals)
  gentle: { type: 'spring', stiffness: 200, damping: 25 } as const,
  // Bouncy: Celebratory/success animations
  bouncy: { type: 'spring', stiffness: 300, damping: 20 } as const,
  // Stiff: Micro-interactions (hover states)
  stiff: { type: 'spring', stiffness: 500, damping: 35 } as const,
}

// ============================================
// DURATION PRESETS
// Non-spring animation durations
// ============================================

export const durations = {
  instant: 0.1,
  fast: 0.2,
  normal: 0.3,
  slow: 0.5,
  slower: 0.8,
}

// ============================================
// STAGGER PRESETS
// List animation stagger timing
// ============================================

export const staggerTiming = {
  fast: 0.03,
  normal: 0.05,
  slow: 0.08,
}

// ============================================
// SCREEN TRANSITION VARIANTS
// Used with AnimatePresence for screen switches
// ============================================

export const screenVariants: Variants = {
  initial: {
    opacity: 0,
    x: 20,
  },
  animate: {
    opacity: 1,
    x: 0,
    transition: springs.gentle,
  },
  exit: {
    opacity: 0,
    x: -20,
    transition: { duration: durations.fast },
  },
}

// Alternative: Fade only (for simpler transitions)
export const fadeVariants: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: durations.normal } },
  exit: { opacity: 0, transition: { duration: durations.fast } },
}

// ============================================
// LIST/STAGGER VARIANTS
// For animating lists of items
// ============================================

export const listVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: staggerTiming.normal,
      delayChildren: 0.1,
    },
  },
}

export const listItemVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: springs.gentle,
  },
}

// ============================================
// CARD VARIANTS
// For card entrance animations
// ============================================

export const cardVariants: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: springs.gentle,
  },
}

// ============================================
// BUTTON/TAP VARIANTS
// For interactive elements
// ============================================

export const tapVariants = {
  tap: { scale: 0.98 },
  hover: { scale: 1.01 },
}

// ============================================
// SUCCESS/CELEBRATION VARIANTS
// For success states and completion
// ============================================

export const successVariants: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.8,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: springs.bouncy,
  },
}

export const checkmarkVariants: Variants = {
  hidden: {
    pathLength: 0,
    opacity: 0,
  },
  visible: {
    pathLength: 1,
    opacity: 1,
    transition: {
      pathLength: { duration: 0.5, ease: 'easeOut' },
      opacity: { duration: 0.2 },
    },
  },
}

// ============================================
// LOADING VARIANTS
// For loading/processing states
// ============================================

export const pulseVariants: Variants = {
  animate: {
    scale: [1, 1.05, 1],
    opacity: [0.5, 1, 0.5],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Get reduced motion safe transition
 * Falls back to instant transition when reduced motion is preferred
 */
export function getReducedMotionTransition(
  transition: Transition,
  reducedMotion: boolean
): Transition {
  if (reducedMotion) {
    return { duration: 0 }
  }
  return transition
}

/**
 * Create staggered children variants with custom timing
 */
export function createStaggerVariants(
  staggerDelay: number = staggerTiming.normal
): Variants {
  return {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: staggerDelay,
      },
    },
  }
}
